import { useMutation, useQuery } from '@apollo/client/react';
import {
  STAFF_BILLING_HARDWARE_PRODUCTS,
  STAFF_BILLING_UPSERT_HARDWARE_PRODUCT,
  STAFF_BILLING_REORDER_HARDWARE_PRODUCTS,
  STAFF_BILLING_DELETE_HARDWARE_PRODUCT,
  STAFF_BILLING_HARDWARE_SHIPPING_TIERS,
  STAFF_BILLING_UPSERT_HARDWARE_SHIPPING_TIER,
  STAFF_BILLING_DELETE_HARDWARE_SHIPPING_TIER,
  STAFF_BILLING_HARDWARE_CATALOG,
} from '@staff-app/graphql-client';
import type { HardwareCatalog } from '@/features/optihub/hooks/useBillingConsole';

/**
 * One row of the staff-owned SKU → Stripe product map.
 *
 * MAPPING ONLY — deliberately no price. Name and price come from Mongo's
 * `deviceProducts` at invoice time so the catalog stays the single source of truth
 * for money. `stripeProductName` is a display cache of what Stripe returned when
 * the id was validated, never used for billing.
 */
export type HardwareProductMapping = {
  /** `deviceProducts._id`, e.g. `proMax`. */
  sku: string;
  /**
   * A Stripe Price id when the row is a CONFIGURATION priced by Stripe, `''` when it
   * is priced from the Mongo catalog. Part of the key with `sku`.
   */
  stripePriceId: string;
  /** Stripe's nickname for that price — the dropdown label. */
  stripePriceNickname?: string | null;
  stripeProductId: string;
  stripeProductName?: string | null;
  /** Position in the invoice dropdown, ascending. 0 = unordered (derived order applies). */
  sortOrder: number;
  isActive: boolean;
  /**
   * May this product be fulfilled through Amazon MCF?
   *
   * A different question from `isActive`: that one says whether we sell it, this says whether we
   * hold stock of it in an Amazon fulfilment centre. It drives the item picker on International
   * Requests, which is why it is edited here rather than in code — it changes when somebody buys
   * stock, not when somebody ships a release.
   */
  mcfEligible: boolean;
  /**
   * May All International ship this product by its China or Houston route? The same kind of
   * purchasing fact as `mcfEligible`, for the factory and the Houston warehouse (one flag: both
   * place the same Shopify SKU and differ only by location).
   */
  shopifyFulfilEligible: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export function useHardwareProducts() {
  return useQuery<{ staffBillingHardwareProducts: HardwareProductMapping[] }>(
    STAFF_BILLING_HARDWARE_PRODUCTS,
    { fetchPolicy: 'cache-and-network' },
  );
}

/**
 * The device catalog, reused here to offer real SKUs instead of a free-text box —
 * a mapping keyed to a SKU that doesn't exist can never be invoiced.
 */
export function useHardwareCatalogForMapping() {
  return useQuery<{ staffBillingHardwareCatalog: HardwareCatalog }>(
    STAFF_BILLING_HARDWARE_CATALOG,
    { fetchPolicy: 'cache-and-network' },
  );
}

export function useUpsertHardwareProduct() {
  return useMutation<
    { staffBillingUpsertHardwareProduct: HardwareProductMapping },
    {
      sku: string;
      stripeProductId: string;
      stripePriceId?: string;
      isActive?: boolean;
      /** Omit to leave it as it is — see the mutation's own comment. */
      mcfEligible?: boolean;
      /** Omit to leave it as it is, as `mcfEligible`. */
      shopifyFulfilEligible?: boolean;
    }
  >(STAFF_BILLING_UPSERT_HARDWARE_PRODUCT, {
    // The catalog's `invoiceable` flags derive from this map, so both must refresh.
    refetchQueries: ['staffBillingHardwareProducts', 'staffBillingHardwareCatalog'],
  });
}

/**
 * Reorder the invoice dropdown. Sends the WHOLE order rather than a "move this one up"
 * delta, so there is no renumbering to apply out of sequence; the mutation returns the rows
 * in their new order.
 */
export function useReorderHardwareProducts() {
  return useMutation<
    { staffBillingReorderHardwareProducts: HardwareProductMapping[] },
    { order: string[] }
  >(STAFF_BILLING_REORDER_HARDWARE_PRODUCTS, {
    // The picker's order comes from the catalog query, so that has to refresh too.
    refetchQueries: ['staffBillingHardwareProducts', 'staffBillingHardwareCatalog'],
  });
}

export function useDeleteHardwareProduct() {
  return useMutation<
    { staffBillingDeleteHardwareProduct: boolean },
    { sku: string; stripePriceId?: string }
  >(STAFF_BILLING_DELETE_HARDWARE_PRODUCT, {
    refetchQueries: ['staffBillingHardwareProducts', 'staffBillingHardwareCatalog'],
  });
}

/**
 * One freight BAND for one product. The only money on this page — and freight only: a band
 * cannot change a product price, which stays in `deviceProducts` or the row's Stripe price.
 *
 * `amountMinor` is the WHOLE charge at that quantity band (2 kiosks = $150 total, not
 * 2 × $75), and `0` legitimately means "ships free from this quantity".
 */
export type HardwareShippingTier = {
  sku: string;
  region: string;
  currency: string;
  minQuantity: number;
  amountMinor: number;
  updatedAt?: string | null;
  updatedBy?: string | null;
};

export function useHardwareShippingTiers() {
  return useQuery<{ staffBillingHardwareShippingTiers: HardwareShippingTier[] }>(
    STAFF_BILLING_HARDWARE_SHIPPING_TIERS,
    { fetchPolicy: 'cache-and-network' },
  );
}

/** Both tier mutations refresh the tier list only — no mapping or catalog price changes. */
const TIER_REFETCH = { refetchQueries: ['staffBillingHardwareShippingTiers'] };

export function useUpsertHardwareShippingTier() {
  return useMutation<
    { staffBillingUpsertHardwareShippingTier: HardwareShippingTier },
    {
      sku: string;
      minQuantity: number;
      amountMinor: number;
      region?: string;
      currency?: string;
    }
  >(STAFF_BILLING_UPSERT_HARDWARE_SHIPPING_TIER, TIER_REFETCH);
}

export function useDeleteHardwareShippingTier() {
  return useMutation<
    { staffBillingDeleteHardwareShippingTier: boolean },
    { sku: string; minQuantity: number; region?: string; currency?: string }
  >(STAFF_BILLING_DELETE_HARDWARE_SHIPPING_TIER, TIER_REFETCH);
}
